import React, { FC, memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Form, Input, Space, Spin, Switch } from 'udesk-ui';
import SemanticTagsSelect from 'src/components/pages/components/semantic-tags-select';
import { Enable } from './const';
import { postTopicSettings } from 'src/api/topicSettings';
import { getTopicSettingsById, putTopicSettingsById } from 'src/api/topicSettings/{id}';
import { PostTopicSettingsBody } from 'src/api/types';
import UdeskLocales from 'UdeskLocales';

const Wrapper = styled.div``;

interface FormValues extends PostTopicSettingsBody {
    isEnable?: boolean;
    tag?: {
        groupId?: number;
        semanticName?: string;
        sentence?: string;
    };
}

interface TemplateProps {
    type?: number;
    onBack?: () => void;
    id?: number;
    taskId?: number;
    lang?: number;
}

const Template: FC<TemplateProps> = memo((props) => {
    const { onBack, id, taskId, lang } = props;

    const [loading, setLoading] = useState(false);

    const [form] = Form.useForm<FormValues>();

    useEffect(() => {
        form.setFieldsValue({
            isEnable: false,
        });

        if (id) {
            setLoading(true);
            getTopicSettingsById({ segments: { id } })
                .then((res) => {
                    form.setFieldsValue({
                        ...res.data,
                        isEnable: res.data?.topicStatus === Enable.OPEN,
                        tag: {
                            groupId: res.data?.tagGroupId,
                            semanticName: res.data?.tagGroup?.title,
                            sentence: res.data?.tagGroup?.standardContent,
                        },
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [form, id]);

    const onFinish = () => {
        if (!taskId) return;

        setLoading(true);
        form.validateFields()
            .then((values) => {
                const params: PostTopicSettingsBody = {
                    taskId,
                    topicName: values.topicName,
                    topicDesc: values.topicDesc,
                    topicStatus: values.isEnable ? Enable.OPEN : Enable.CLOSE,
                    tagGroupId: values.tag?.groupId,
                };

                const p = id
                    ? putTopicSettingsById(params, { segments: { id } })
                    : postTopicSettings(params);

                p.then(() => {
                    onBack?.();
                }).finally(() => {
                    setLoading(false);
                });
            })
            .catch(() => {
                setLoading(false);
            });
    };

    return (
        <Wrapper>
            <Spin spinning={loading}>
                <Form<FormValues>
                    form={form}
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 20 }}
                    onFinish={onFinish}
                >
                    <Form.Item
                        label={
                            /* 话题名称 */ UdeskLocales['current'].pages.tasks.manage.template
                                .automaticAdd.topicAnalysis.components.field.index.topicName
                        }
                        name="topicName"
                        rules={[{ required: true }, { max: 20 }]}
                    >
                        <Input style={{ width: 500 }} />
                    </Form.Item>

                    <Form.Item
                        label={
                            /* 话题描述 */ UdeskLocales['current'].pages.tasks.manage.template
                                .automaticAdd.topicAnalysis.components.field.index.topicDescription
                        }
                        name="topicDesc"
                        rules={[{ max: 400 }]}
                    >
                        <Input.TextArea style={{ width: 500 }} autoSize />
                    </Form.Item>

                    <Form.Item
                        label={
                            /* 是否启用 */ UdeskLocales['current'].pages.tasks.manage.template
                                .automaticAdd.businessSummary.components.field.index.enabled
                        }
                        name="isEnable"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        label={
                            /* 话题标签 */ UdeskLocales['current'].pages.tasks.manage.template
                                .automaticAdd.topicAnalysis.components.field.index.topicTag
                        }
                        name="tag"
                        rules={[{ required: true }]}
                    >
                        <SemanticTagsSelect lang={lang} />
                    </Form.Item>

                    <Form.Item wrapperCol={{ offset: 4 }}>
                        <Space>
                            <Button onClick={onBack}>
                                {/* 返回 */}
                                {
                                    UdeskLocales['current'].pages.tasks.manage.template.automaticAdd
                                        .businessSummary.components.field.index.return
                                }
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {/* 保存 */}
                                {
                                    UdeskLocales['current'].pages.tasks.manage.template.automaticAdd
                                        .businessSummary.components.field.index.preserve
                                }
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Spin>
        </Wrapper>
    );
});

export default Template;
